import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';
import { AppConfig } from '@/datasources/AppConfig';
import { encryptToken, decryptToken } from '@/utils/tokenCrypto';

// Interface para o token de autenticação
interface TokenResposta {
  tokenAcesso: string;
  tokenAtualizacao: string;
  emitido: string;
  expira: string;
  usuario: {
    id: string;
    nome: string;
    email?: string;
    telefone?: string;
    confirmado: boolean;
    foto?: string;
  };
}

// Interface para credenciais
interface CredenciaisUsuario {
  id: string;
  senha: string;
}

// Interface para credenciais Google
interface CredenciaisGoogle {
  idToken: string;
}

// Interface para criar usuário
interface CriarUsuarioRequest {
  id: string;
  nome: string;
  vincularLogin?: {
    conta: 'Google' | 'Facebook' | 'Apple';
    idToken: string;
  };
}

// Interface para redefinir senha
interface RedefinirSenhaRequest {
  id: string;
  codigo: string;
  senha: string;
  confirmacaoSenha?: string;
}

class AutenticacaoService {
  private readonly remoteDataSource: RemoteDataSource;

  constructor() {
    this.remoteDataSource = new RemoteDataSource(Settings.urlSecondary);
  }

  /**
   * Efetua a autenticação do usuário
   * POST /autenticacao/v1/autenticar
   */
  async autenticar(credenciais: CredenciaisUsuario): Promise<{ success: TokenResposta } | { failure: Error }> {
    try {
      const response = await this.remoteDataSource.post<TokenResposta>({
        endpoint: 'v1/autenticar',
        body: credenciais as unknown as Record<string, unknown>,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        requiredToken: false,
      });

      if ('failure' in response) {
        const failure = response.failure;
        const errorMessage = failure.message || 'Erro na autenticação';
        return { failure: new Error(errorMessage) };
      }

      if (!response.success.body || typeof response.success.body !== 'object') {
        return { failure: new Error('Resposta inválida do servidor') };
      }

      // Verificar se o body tem as propriedades esperadas
      const body = response.success.body as TokenResposta;
      if (!body.tokenAcesso || !body.tokenAtualizacao || !body.usuario) {
        return { failure: new Error('Resposta incompleta do servidor') };
      }

      return { success: body };
    } catch (error) {
      return { failure: error instanceof Error ? error : new Error('Erro desconhecido na autenticação') };
    }
  }

  /**
   * Efetua a autenticação do usuário através do Google
   * POST /autenticacao/v1/autenticar-google
   */
  async autenticarGoogle(credenciais: CredenciaisGoogle): Promise<{ success: TokenResposta } | { failure: Error }> {
    try {
      const response = await this.remoteDataSource.post<TokenResposta>({
        endpoint: 'v1/autenticar-google',
        body: credenciais as unknown as Record<string, unknown>,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        requiredToken: false,
      });

      if ('failure' in response) {
        const failure = response.failure;
        const errorMessage = failure.message || 'Erro na autenticação com Google';
        return { failure: new Error(errorMessage) };
      }

      if (!response.success.body || typeof response.success.body !== 'object') {
        return { failure: new Error('Resposta inválida do servidor') };
      }

      const body = response.success.body as TokenResposta;
      if (!body.tokenAcesso || !body.tokenAtualizacao || !body.usuario) {
        return { failure: new Error('Resposta incompleta do servidor') };
      }

      return { success: body };
    } catch (error) {
      return { failure: error instanceof Error ? error : new Error('Erro desconhecido na autenticação com Google') };
    }
  }

  /**
   * Cria um novo usuário
   * POST /autenticacao/v1/usuarios
   */
  async criarUsuario(usuario: CriarUsuarioRequest): Promise<{ success: TokenResposta } | { failure: Error }> {
    try {
      const response = await this.remoteDataSource.post<TokenResposta>({
        endpoint: 'v1/usuarios',
        body: usuario as unknown as Record<string, unknown>,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        requiredToken: false,
      });

      if ('failure' in response) {
        const failure = response.failure;
        const errorMessage = failure.message || 'Erro ao criar usuário';
        return { failure: new Error(errorMessage) };
      }

      if (!response.success.body || typeof response.success.body !== 'object') {
        return { failure: new Error('Resposta inválida do servidor') };
      }

      // Verificar se o body tem as propriedades esperadas
      const body = response.success.body as TokenResposta;
      if (!body.tokenAcesso || !body.tokenAtualizacao || !body.usuario) {
        return { failure: new Error('Resposta incompleta do servidor') };
      }

      return { success: body };
    } catch (error) {
      return { failure: error instanceof Error ? error : new Error('Erro desconhecido ao criar usuário') };
    }
  }

  /**
   * Redefine a senha do usuário
   * POST /autenticacao/v1/usuarios/redefinir-senha
   */
  async redefinirSenha(redefinirSenha: RedefinirSenhaRequest): Promise<{ success: boolean } | { failure: Error }> {
    try {
      const response = await this.remoteDataSource.post<unknown>({
        endpoint: '/autenticacao/v1/usuarios/redefinir-senha',
        body: redefinirSenha as unknown as Record<string, unknown>,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        requiredToken: false,
      });

      if ('failure' in response) {
        const failure = response.failure;
        const errorMessage = failure.message || 'Erro ao redefinir senha';
        return { failure: new Error(errorMessage) };
      }

      return { success: true };
    } catch (error) {
      return { failure: error instanceof Error ? error : new Error('Erro desconhecido ao redefinir senha') };
    }
  }

  /**
   * Salva o token no localStorage
   */
  async salvarToken(token: TokenResposta): Promise<void> {
    // Token encriptado (AES-GCM) em disco (localStorage) para evitar exposição casual
    const [tokenAcessoCifrado, tokenAtualizacaoCifrado] = await Promise.all([
      encryptToken(token.tokenAcesso),
      encryptToken(token.tokenAtualizacao),
    ]);

    localStorage.setItem('tokenAcesso', tokenAcessoCifrado);
    localStorage.setItem('tokenAtualizacao', tokenAtualizacaoCifrado);
    localStorage.setItem('tokenExpira', token.expira);
    localStorage.setItem('usuario', JSON.stringify(token.usuario));
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('userEmail', token.usuario.email || token.usuario.id);

    // Token em texto plano apenas em memória (AppConfig + Settings) para uso nas requisições
    AppConfig.tokenAcesso = token.tokenAcesso;
    AppConfig.refreshToken = token.tokenAtualizacao;
    AppConfig.dataExpiracao = token.expira;
    AppConfig.token = { accessToken: token.tokenAcesso };
    Settings.token = AppConfig.token;
  }

  /**
   * Remove o token do localStorage
   */
  removerToken(): void {
    localStorage.removeItem('tokenAcesso');
    localStorage.removeItem('tokenAtualizacao');
    localStorage.removeItem('tokenExpira');
    localStorage.removeItem('usuario');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    // Limpa cache do agente atual
    localStorage.removeItem('agente');
    localStorage.removeItem('agenteId');
    localStorage.removeItem('ouvidoriaId');
    localStorage.removeItem('municipioId');
    // Limpa aceite de termos para que apareça novamente no próximo login
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('urbnet:termosAceitos:')) {
        localStorage.removeItem(key);
      }
    });

    AppConfig.tokenAcesso = undefined;
    AppConfig.refreshToken = undefined;
    AppConfig.dataExpiracao = undefined;
    AppConfig.token = undefined;
    Settings.token = undefined;
    AppConfig.agenteId = undefined as unknown as string;
    AppConfig.ouvidoriaId = undefined as unknown as string;
    AppConfig.municipioId = undefined as unknown as string;
    AppConfig.municipioLong = undefined as unknown as number;
  }

  /**
   * Obtém o token de acesso (em texto plano).
   * Prioriza o token em memória; faz fallback para o localStorage decriptando.
   */
  async getTokenAcesso(): Promise<string | null> {
    if (AppConfig.tokenAcesso) return AppConfig.tokenAcesso;

    const encrypted = localStorage.getItem('tokenAcesso');
    if (!encrypted) return null;

    const decrypted = await decryptToken(encrypted);
    if (decrypted) {
      AppConfig.tokenAcesso = decrypted;
      AppConfig.token = { accessToken: decrypted };
      Settings.token = AppConfig.token;
    }
    return decrypted || null;
  }

  /**
   * Obtém o usuário logado
   */
  getUsuario(): TokenResposta['usuario'] | null {
    const usuarioStr = localStorage.getItem('usuario');
    return usuarioStr ? JSON.parse(usuarioStr) : null;
  }

  /**
   * Verifica se o usuário está autenticado
   */
  estaAutenticado(): boolean {
    return (
      localStorage.getItem('isAuthenticated') === 'true' &&
      (!!AppConfig.tokenAcesso || !!localStorage.getItem('tokenAcesso'))
    );
  }

  /**
   * Verifica se o token está expirado
   */
  tokenExpirado(): boolean {
    const expira = localStorage.getItem('tokenExpira');
    if (!expira) return true;

    return new Date(expira) <= new Date();
  }

  /**
   * Atualiza o token de acesso usando o token de atualização
   * POST /autenticacao/v1/atualizar-token
   */
  async atualizarToken(): Promise<{ success: TokenResposta } | { failure: Error }> {
    try {
      const tokenAcesso = await this.getTokenAcesso();
      const refreshToken = localStorage.getItem('tokenAtualizacao');

      if (!tokenAcesso || !refreshToken) {
        return { failure: new Error('Tokens não encontrados') };
      }

      const response = await this.remoteDataSource.post<TokenResposta>({
        endpoint: 'v1/atualizar-token',
        body: {
          tokenAcesso,
          tokenAtualizacao: refreshToken,
        } as unknown as Record<string, unknown>,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        requiredToken: false,
      });

      if ('failure' in response) {
        const failure = response.failure;
        const errorMessage = failure.message || 'Erro ao atualizar token';
        return { failure: new Error(errorMessage) };
      }

      if (!response.success.body || typeof response.success.body !== 'object') {
        return { failure: new Error('Resposta inválida do servidor') };
      }

      const body = response.success.body as TokenResposta;
      if (!body.tokenAcesso || !body.tokenAtualizacao || !body.usuario) {
        return { failure: new Error('Resposta incompleta do servidor') };
      }

      // Salvar o novo token
      await this.salvarToken(body);

      return { success: body };
    } catch (error) {
      return { failure: error instanceof Error ? error : new Error('Erro desconhecido ao atualizar token') };
    }
  }
}

// Exporta uma instância singleton do serviço
export const autenticacaoService = new AutenticacaoService();
